// @ts-nocheck
import React, { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { differenceInDays, parseISO } from 'date-fns'
import ProtectedRoute from '@/components/ProtectedRoute'
import { VacationBalanceCard, VacationBalanceCardSkeleton } from '@/components/VacationBalanceCard'
import { VacationAlertsContainer } from '@/components/VacationAlertBanner'
import { VacationRequestList, VacationRequestCardSkeleton } from '@/components/VacationRequestCard'
import { useVacationBalance, useVacationRequests } from '@/hooks/useVacationBalance'
import { ArrowRight, RefreshCw } from 'lucide-react'
import type { EmployeeVacationAlert } from '@/types/vacation'

const VacationOverviewPage: React.FC = () => {
  const navigate = useNavigate()
  const { data: balanceData, isLoading: isLoadingBalance, error: balanceError, refetch: refetchBalance } = useVacationBalance()
  const { data: requestsData, isLoading: isLoadingRequests, error: requestsError, refetch: refetchRequests } = useVacationRequests(5)

  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set())

  // Get primary balance (most recent period)
  const primaryBalance = balanceData?.balances?.[0]

  // Calculate alerts based on balance data
  const alerts = useMemo((): EmployeeVacationAlert[] => {
    if (!primaryBalance) return []

    const alerts: EmployeeVacationAlert[] = []
    const daysUntilExpiration = differenceInDays(
      parseISO(primaryBalance.expirationDate),
      new Date()
    )

    // Critical alert: expiring soon (< 30 days)
    if (daysUntilExpiration > 0 && daysUntilExpiration < 30 && primaryBalance.daysAvailable > 0) {
      alerts.push({
        id: 'expiring-soon',
        severity: 'critical',
        message: `Atenção! Seus ${primaryBalance.daysAvailable} dias de férias expiram em ${daysUntilExpiration} dias`,
        dismissible: true
      })
    }

    // Warning alert: high balance (> 30 days)
    if (primaryBalance.daysAvailable > 30 && daysUntilExpiration >= 30) {
      alerts.push({
        id: 'high-balance',
        severity: 'warning',
        message: `Você tem ${primaryBalance.daysAvailable} dias de férias disponíveis. Planeje suas férias!`,
        dismissible: true
      })
    }

    // Info alert: pending requests
    const pendingCount = requestsData?.requests?.filter(r => r.status === 'PENDING').length || 0
    if (pendingCount > 0) {
      alerts.push({
        id: 'pending-requests',
        severity: 'info',
        message: `Você tem ${pendingCount} solicitação(ões) pendente(s) aguardando aprovação`,
        dismissible: true
      })
    }

    return alerts.filter(alert => !dismissedAlerts.has(alert.id))
  }, [primaryBalance, requestsData, dismissedAlerts])

  const handleDismissAlert = (alertId: string) => {
    setDismissedAlerts(prev => new Set(prev).add(alertId))
  }

  const handleRequestVacation = () => {
    navigate('/vacation/request')
  }

  const handleViewAllRequests = () => {
    navigate('/vacation/requests')
  }

  const handleRetry = async () => {
    await Promise.all([refetchBalance(), refetchRequests()])
  }

  return (
    <ProtectedRoute>
      
        <div className="min-h-screen bg-brand-dark text-white">
          <div className="max-w-7xl mx-auto px-6 py-12">
            {/* Header */}
            <div className="mb-6">
              <nav className="text-sm text-brand-gray mb-2">
                <span>Dashboard</span>
                <span className="mx-2">›</span>
                <span>Férias</span>
                <span className="mx-2">›</span>
                <span className="text-white font-medium">Visão Geral</span>
              </nav>
              <h1 className="text-3xl font-display font-bold text-brand-primary">
                Visão Geral de Férias
              </h1>
            </div>

            {/* Alerts */}
            {alerts.length > 0 && (
              <VacationAlertsContainer
                alerts={alerts}
                onDismiss={handleDismissAlert}
                className="mb-6"
              />
            )}

            {/* Error State */}
            {/* {(balanceError || requestsError) && (
              <div className="bg-error/10 border border-error/30 rounded-monumenta p-6 mb-6">
                <h3 className="text-lg font-display font-semibold text-error mb-2">
                  Erro ao carregar dados
                </h3>
                <p className="text-sm text-brand-gray mb-4">
                  {balanceError?.message || requestsError?.message || 'Ocorreu um erro ao carregar as informações'}
                </p>
                <button
                  onClick={handleRetry}
                  className="btn-danger"
                >
                  <RefreshCw className="w-4 h-4" />
                  Tentar Novamente
                </button>
              </div>
            )} */}

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Balance Card (2/3 width on desktop) */}
              <div className="lg:col-span-2">
                {isLoadingBalance ? (
                  <VacationBalanceCardSkeleton />
                ) : primaryBalance ? (
                  <VacationBalanceCard
                    balance={primaryBalance}
                    onRequestVacation={handleRequestVacation}
                  />
                ) : (
                  <div className="card text-center py-12">
                    <Calendar className="mx-auto h-16 w-16 text-brand-gray mb-4" />
                    <h3 className="font-display text-lg font-semibold text-white mb-2">
                      Nenhum saldo de férias disponível
                    </h3>
                    <p className="text-brand-gray">
                      Entre em contato com o RH para mais informações
                    </p>
                  </div>
                )}

                {/* Additional Balance Periods */}
                {balanceData && balanceData.balances.length > 1 && (
                  <div className="mt-6">
                    <h2 className="text-xl font-display font-semibold text-white mb-4">
                      Períodos Anteriores
                    </h2>
                    <div className="space-y-4">
                      {balanceData.balances.slice(1).map((balance) => (
                        <VacationBalanceCard
                          key={`${balance.periodYear}-${balance.employeeId}`}
                          balance={balance}
                          className="opacity-80"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Recent Requests (1/3 width on desktop) */}
              <div className="lg:col-span-1">
                <div className="card">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-display font-semibold text-white">
                      Minhas Solicitações
                    </h2>
                    {requestsData && requestsData.requests.length > 0 && (
                      <button
                        onClick={handleViewAllRequests}
                        className="btn-ghost text-sm text-brand-primary"
                      >
                        Ver todas
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {isLoadingRequests ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <VacationRequestCardSkeleton key={i} />
                      ))}
                    </div>
                  ) : (
                    <VacationRequestList
                      requests={requestsData?.requests || []}
                      isLoading={false}
                      onRequestClick={() => {}}
                      onCancelRequest={() => {}}
                    />
                  )}

                  {/* CTA to create first request */}
                  {requestsData && requestsData.requests.length === 0 && !isLoadingRequests && (
                    <button
                      onClick={handleRequestVacation}
                      className="btn-primary w-full mt-4"
                    >
                      Criar Primeira Solicitação
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      
    </ProtectedRoute>
  )
}

export default VacationOverviewPage
